import {useUserContext} from '@hconnect/login'
import {
  Alert,
  Button,
  LabelRegular,
  UserMenu,
  CombinedTitleBar,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  TextRegular
} from '@hm/fundamental'
import {mdiMenu, mdiClose} from '@mdi/js'
import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Persona} from '../../../api/types/personas'
import {useSwitchPersona} from '../../../app/welcomeFlow/usePersonas'
import HProduceLogo from '../../../assets/logo.svg'

import {FeatureBreadcrumbs} from './FeatureBreadcrumbs'
import {MobileMenuContent} from './MobileMenuContent'
import {PlantSelectButton} from './PlantSelectButton'
import {useUserMenuItems} from './useUserMenuItems'

type HeaderProps = {
  isLeftMenuOpen: boolean
  setIsLeftMenuOpen: (isLeftMenuOpen: boolean) => void
}

export const HProduceSuiteHeader: React.FC<HeaderProps> = ({isLeftMenuOpen, setIsLeftMenuOpen}) => {
  const {t} = useTranslation()

  const {loggedUser} = useUserContext()
  const [pendingPersona, setPendingPersona] = useState<Persona>()
  const {mutate: switchPersona, isPending: isSwitchingPersona} = useSwitchPersona()

  const menuItems = useUserMenuItems({onSelectPersona: setPendingPersona})

  const handleMenuClose = useCallback(() => {
    setIsLeftMenuOpen(false)
  }, [setIsLeftMenuOpen])

  const closeRoleSwitchDialog = useCallback(() => {
    if (!isSwitchingPersona) {
      setPendingPersona(undefined)
    }
  }, [isSwitchingPersona])

  const confirmRoleSwitch = useCallback(() => {
    if (!pendingPersona) {
      return
    }

    switchPersona(pendingPersona.id, {onSuccess: () => setPendingPersona(undefined)})
  }, [pendingPersona, switchPersona])

  const userMenu = (
    <div data-cy="user_menu" key="user_menu">
      <UserMenu
        loggedIn={!!loggedUser}
        items={menuItems}
        footer={
          <div className="flex flex-col items-center">
            <LabelRegular className="text-foreground_faded">
              {t('suite.header.copyright')}
            </LabelRegular>
            <LabelRegular className="text-foreground_faded">
              <a href="https://heidelbergmaterials.com">
                {t('suite.header.heidelbergmaterials', {date: new Date().getFullYear()})}
              </a>
            </LabelRegular>
          </div>
        }
      />
    </div>
  )

  return (
    <>
      <CombinedTitleBar
        logo={<img src={HProduceLogo} data-cy="hproduce_logo" alt="HProduce logo" />}
        breadcrumbs={<FeatureBreadcrumbs />}
        data-cy="header"
        menuButton={
          <>
            {loggedUser && (
              <Button
                variant="secondary"
                icon={isLeftMenuOpen ? mdiClose : mdiMenu}
                onClick={() => setIsLeftMenuOpen(!isLeftMenuOpen)}
                round
                square
                data-icon={isLeftMenuOpen ? 'icon_close' : 'icon_menu'}
                data-cy="menu_toggle"
                data-test-id="menu_toggle"
              />
            )}
          </>
        }
        mobileMenuContent={
          isLeftMenuOpen ? <MobileMenuContent onMenuClose={handleMenuClose} /> : undefined
        }
        desktopToolbar={
          loggedUser
            ? [<PlantSelectButton key="plant-select" data-cy="plant-select-button" />, userMenu]
            : [userMenu]
        }
        mobileToolbar={userMenu}
      />
      <Modal
        open={!!pendingPersona}
        onClose={closeRoleSwitchDialog}
        isExitEnabled={!isSwitchingPersona}
        data-cy="persona_switch_modal"
      >
        <ModalTitle>{t('suite.roleSwitch.confirmTitle')}</ModalTitle>
        <ModalContent>
          <Alert variant="warning" data-cy="persona_switch_warning">
            <TextRegular>{t('suite.roleSwitch.confirmBody')}</TextRegular>
          </Alert>
        </ModalContent>
        <ModalFooter justifyClass="justify-end">
          <Button
            variant="secondary"
            onClick={closeRoleSwitchDialog}
            disabled={isSwitchingPersona}
            data-cy="persona_switch_cancel"
          >
            {t('suite.roleSwitch.cancel')}
          </Button>
          <Button
            variant="negative"
            onClick={confirmRoleSwitch}
            disabled={isSwitchingPersona}
            data-cy="persona_switch_confirm"
          >
            {t('suite.roleSwitch.confirmCta')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}
